"use strict";
// Movie Cache Service - Firestore-based caching for movie data
// Dramatically reduces TMDB API calls by sharing cached data across all users
// Uses UnifiedFirestore for cross-platform compatibility (web SDK on browser, native plugin on iOS/Android)
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.movieCacheService = void 0;
const firebaseBase_1 = require("./firebaseBase");
const unifiedFirestoreService_1 = require("./unifiedFirestoreService");
const core_1 = require("@capacitor/core");
const movieUtils_1 = require("../utils/movieUtils");
/**
 * Movie Cache Service
 *
 * Stores movie data in Firestore to minimize TMDB API calls.
 * Benefits all users - one API call caches data for everyone.
 *
 * Key features:
 * - Automatic caching on first access
 * - Batch operations for efficiency
 * - 90-day TTL (movies rarely change)
 * - Access statistics for optimization
 * - Automatic cleanup of expired entries
 */
class MovieCacheService {
    constructor() {
        this.CACHE_COLLECTION = 'movieCache';
        this.CACHE_TTL = 90 * 24 * 60 * 60 * 1000; // 90 days in milliseconds
        this.CACHE_VERSION = 1;
        this.FIRESTORE_BATCH_SIZE = 10; // Firestore 'in' query limit
    }
    /**
     * Get movie data from cache or fetch from TMDB
     * @param movieId - TMDB movie ID
     * @returns Movie cache data
     */
    getMovieData(movieId) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const cacheId = (_a = (0, movieUtils_1.getTmdbNumericIdForCache)(movieId)) !== null && _a !== void 0 ? _a : movieId;
            console.log(`🎬 MovieCacheService: Getting movie data for ${movieId} (cache key: ${cacheId})`);
            // 1. Try to get from Firestore cache
            const cached = yield this.getFromCache(cacheId);
            if (cached && !this.isExpired(cached)) {
                console.log(`📦 Cache HIT for movie ${cacheId} (age: ${this.getAge(cached)})`);
                // Update access statistics (non-blocking)
                this.updateAccessStats(cacheId).catch(err => console.warn(`Failed to update access stats for ${cacheId}:`, err));
                return cached;
            }
            // 2. Cache miss or expired - fetch from TMDB
            console.log(`🌐 Cache MISS for movie ${cacheId} - fetching from TMDB`);
            const tmdbData = yield this.fetchFromTMDB(cacheId);
            // 3. Store in cache for future use
            yield this.saveToCache(cacheId, tmdbData);
            return tmdbData;
        });
    }
    /**
     * Get multiple movies in batch (optimized)
     * @param movieIds - Array of TMDB movie IDs
     * @returns Array of movie cache data
     */
    getBatchMovieData(movieIds) {
        return __awaiter(this, void 0, void 0, function* () {
            if (movieIds.length === 0)
                return [];
            console.log(`📦 MovieCacheService: Batch getting ${movieIds.length} movies`);
            const results = [];
            const uncachedIds = [];
            // Process in batches due to Firestore 'in' query limit of 10
            // On native, we use individual document fetches to avoid 'in' query issues
            const isNative = core_1.Capacitor.isNativePlatform();
            if (isNative) {
                // Native platform: fetch documents individually using unified service
                // The 'in' query operator can be unreliable on native, so fetch one by one
                console.log(`📱 MovieCacheService: Using native path - fetching ${movieIds.length} docs individually`);
                const fetchPromises = movieIds.map((id) => __awaiter(this, void 0, void 0, function* () {
                    var _a;
                    const cacheId = (_a = (0, movieUtils_1.getTmdbNumericIdForCache)(id)) !== null && _a !== void 0 ? _a : id;
                    try {
                        const cached = yield this.getFromCache(cacheId);
                        if (cached && !this.isExpired(cached)) {
                            results.push(cached);
                        }
                        else {
                            if (cached)
                                console.log(`⏰ Cache entry expired for ${cacheId}`);
                            uncachedIds.push(cacheId);
                        }
                    }
                    catch (error) {
                        console.warn(`Error fetching cache for ${cacheId}:`, error);
                        uncachedIds.push(cacheId);
                    }
                }));
                yield Promise.all(fetchPromises);
            }
            else {
                // Web platform: use batch 'in' queries for efficiency
                for (let i = 0; i < movieIds.length; i += this.FIRESTORE_BATCH_SIZE) {
                    const batch = movieIds.slice(i, i + this.FIRESTORE_BATCH_SIZE);
                    const normalizedBatch = batch.map(id => { var _a; return (_a = (0, movieUtils_1.getTmdbNumericIdForCache)(id)) !== null && _a !== void 0 ? _a : id; });
                    try {
                        const uniqueDocIds = [...new Set(normalizedBatch)];
                        const q = (0, firebaseBase_1.query)((0, firebaseBase_1.collection)((0, firebaseBase_1.getDb)(), this.CACHE_COLLECTION), (0, firebaseBase_1.where)('__name__', 'in', uniqueDocIds));
                        const querySnapshot = yield (0, firebaseBase_1.getDocs)(q);
                        const cachedMap = new Map();
                        querySnapshot.forEach(docSnap => {
                            const data = docSnap.data();
                            if (!this.isExpired(data)) {
                                cachedMap.set(docSnap.id, data);
                            }
                            else {
                                console.log(`⏰ Cache entry expired for ${docSnap.id}`);
                            }
                        });
                        normalizedBatch.forEach(cacheId => {
                            const cached = cachedMap.get(cacheId);
                            if (cached) {
                                results.push(cached);
                            }
                            else {
                                uncachedIds.push(cacheId);
                            }
                        });
                    }
                    catch (error) {
                        console.error(`Error querying cache batch:`, error);
                        uncachedIds.push(...normalizedBatch);
                    }
                }
            }
            console.log(`📊 Cache stats: ${results.length} cached, ${uncachedIds.length} uncached`);
            // Fetch uncached movies from TMDB
            if (uncachedIds.length > 0) {
                console.log(`🌐 Fetching ${uncachedIds.length} movies from TMDB`);
                const tmdbResults = yield this.fetchBatchFromTMDB(uncachedIds);
                // Store in cache for future use
                if (tmdbResults.length > 0) {
                    yield this.saveBatchToCache(tmdbResults);
                }
                results.push(...tmdbResults);
            }
            console.log(`✅ Batch complete: returned ${results.length}/${movieIds.length} movies`);
            return results;
        });
    }
    /**
     * Get minimal movie data for quick display
     * @param movieId - TMDB movie ID
     * @returns Minimal movie data or null
     */
    getMinimalMovieData(movieId) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const cacheId = (_a = (0, movieUtils_1.getTmdbNumericIdForCache)(movieId)) !== null && _a !== void 0 ? _a : movieId;
                const cached = yield this.getFromCache(cacheId);
                if (!cached)
                    return null;
                return {
                    id: cached.id,
                    title: cached.title,
                    year: cached.year,
                    posterPath: cached.posterPath,
                    genres: cached.genres.map(g => g.name),
                };
            }
            catch (error) {
                console.error(`Error getting minimal movie data for ${movieId}:`, error);
                return null;
            }
        });
    }
    /**
     * Check if a movie is cached
     * @param movieId - TMDB movie ID
     * @returns True if cached and not expired
     */
    isCached(movieId) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const cacheId = (_a = (0, movieUtils_1.getTmdbNumericIdForCache)(movieId)) !== null && _a !== void 0 ? _a : movieId;
                const cached = yield this.getFromCache(cacheId);
                return cached !== null && !this.isExpired(cached);
            }
            catch (error) {
                return false;
            }
        });
    }
    /**
     * Get cache statistics
     * @returns Cache statistics
     */
    getCacheStats() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                // Use unifiedCollection for cross-platform support
                const allDocs = yield (0, unifiedFirestoreService_1.unifiedCollection)(this.CACHE_COLLECTION).getDocs();
                let oldestDate = null;
                let newestDate = null;
                const moviesByAccess = [];
                allDocs.forEach((docData) => {
                    const data = docData.data();
                    const cacheDate = this.timestampToDate(data.cachedAt);
                    if (cacheDate) {
                        if (!oldestDate || cacheDate < oldestDate)
                            oldestDate = cacheDate;
                        if (!newestDate || cacheDate > newestDate)
                            newestDate = cacheDate;
                    }
                    if (data.accessCount) {
                        moviesByAccess.push({
                            id: data.id,
                            title: data.title,
                            accessCount: data.accessCount,
                        });
                    }
                });
                // Sort by access count
                moviesByAccess.sort((a, b) => b.accessCount - a.accessCount);
                return {
                    totalCachedMovies: allDocs.length,
                    cacheHitRate: 0,
                    mostAccessedMovies: moviesByAccess.slice(0, 10),
                    oldestCacheEntry: oldestDate,
                    newestCacheEntry: newestDate,
                };
            }
            catch (error) {
                console.error('Error getting cache stats:', error);
                return {
                    totalCachedMovies: 0,
                    cacheHitRate: 0,
                    mostAccessedMovies: [],
                    oldestCacheEntry: null,
                    newestCacheEntry: null,
                };
            }
        });
    }
    /**
     * Convert timestamp to Date object (handles various formats)
     */
    timestampToDate(timestamp) {
        if (!timestamp)
            return null;
        const ms = this.timestampToMillis(timestamp);
        return ms > 0 ? new Date(ms) : null;
    }
    /**
     * Bypass the cache and fetch fresh data from TMDB, then update the cache.
     * Use this when cached data is known to be incomplete (e.g. backdropPath is null).
     */
    refreshMovieData(movieId) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const cacheId = (_a = (0, movieUtils_1.getTmdbNumericIdForCache)(movieId)) !== null && _a !== void 0 ? _a : movieId;
            console.log(`🔄 MovieCacheService: Force-refreshing ${cacheId} from TMDB`);
            const tmdbData = yield this.fetchFromTMDB(cacheId);
            yield this.saveToCache(cacheId, tmdbData);
            return tmdbData;
        });
    }
    /**
     * Cleanup expired cache entries
     * Should be called by a scheduled Firebase Function
     * @returns Number of entries deleted
     */
    cleanupExpiredCache() {
        return __awaiter(this, void 0, void 0, function* () {
            console.log('🧹 Starting cache cleanup...');
            try {
                const allDocs = yield (0, unifiedFirestoreService_1.unifiedCollection)(this.CACHE_COLLECTION).getDocs();
                let deleted = 0;
                let kept = 0;
                yield Promise.all(allDocs.map((d) => __awaiter(this, void 0, void 0, function* () {
                    const data = d.data();
                    if (this.isExpired(data)) {
                        try {
                            yield (0, unifiedFirestoreService_1.unifiedDeleteDoc)(this.CACHE_COLLECTION, d.id);
                        }
                        catch (_) { }
                        deleted++;
                    }
                    else {
                        kept++;
                    }
                })));
                console.log(deleted > 0
                    ? `🧹 Cleanup complete: deleted ${deleted}, kept ${kept}`
                    : `✅ No expired entries found (${kept} entries still valid)`);
                return { deleted, kept };
            }
            catch (error) {
                console.error('Error during cache cleanup:', error);
                throw error;
            }
        });
    }
    // ============== PRIVATE METHODS ==============
    /**
     * Get movie from Firestore cache
     * Uses unified Firestore service - routes through native plugin on iOS/Android
     */
    getFromCache(movieId) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const docSnap = yield (0, unifiedFirestoreService_1.unifiedGetDoc)(this.CACHE_COLLECTION, movieId);
                if (docSnap.exists()) {
                    return docSnap.data();
                }
                return null;
            }
            catch (error) {
                console.error(`Error reading cache for ${movieId}:`, error);
                return null;
            }
        });
    }
    /**
     * Save movie to Firestore cache
     * Uses unified Firestore service for cross-platform compatibility
     */
    saveToCache(movieId, data) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                // Sanitize the data to remove undefined values (Firestore doesn't accept undefined)
                const sanitizedData = this.sanitizeForFirestore(data);
                yield (0, unifiedFirestoreService_1.unifiedSetDoc)(this.CACHE_COLLECTION, movieId, sanitizedData);
                console.log(`💾 Saved movie ${movieId} to cache`);
            }
            catch (error) {
                console.error(`Error saving to cache for ${movieId}:`, error);
                throw error;
            }
        });
    }
    /**
     * Save batch of movies to cache
     */
    saveBatchToCache(movies) {
        return __awaiter(this, void 0, void 0, function* () {
            if (movies.length === 0)
                return;
            try {
                yield Promise.all(movies.map(movie => {
                    const sanitizedMovie = this.sanitizeForFirestore(movie);
                    return (0, unifiedFirestoreService_1.unifiedSetDoc)(this.CACHE_COLLECTION, movie.id, sanitizedMovie);
                }));
                console.log(`💾 Saved ${movies.length} movies to cache (batch)`);
            }
            catch (error) {
                console.error('Error saving batch to cache:', error);
                throw error;
            }
        });
    }
    /**
     * Sanitize data for Firestore - removes undefined values and replaces with null
     */
    sanitizeForFirestore(data) {
        if (data === undefined)
            return null;
        if (data === null)
            return null;
        if (typeof data !== 'object')
            return data;
        if (data instanceof firebaseBase_1.Timestamp)
            return data;
        if (Array.isArray(data)) {
            return data.map(item => this.sanitizeForFirestore(item));
        }
        const sanitized = {};
        for (const [key, value] of Object.entries(data)) {
            if (value !== undefined) {
                sanitized[key] = this.sanitizeForFirestore(value);
            }
        }
        return sanitized;
    }
    /**
     * Update access statistics for a movie
     * Uses unified Firestore service for cross-platform compatibility
     */
    updateAccessStats(movieId) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const cacheId = (_a = (0, movieUtils_1.getTmdbNumericIdForCache)(movieId)) !== null && _a !== void 0 ? _a : movieId;
            try {
                const docSnap = yield (0, unifiedFirestoreService_1.unifiedGetDoc)(this.CACHE_COLLECTION, cacheId);
                if (docSnap.exists()) {
                    const currentCount = docSnap.data().accessCount || 0;
                    yield (0, unifiedFirestoreService_1.unifiedSetDoc)(this.CACHE_COLLECTION, cacheId, {
                        accessCount: currentCount + 1,
                        lastAccessedAt: new Date().toISOString(),
                    }, true);
                }
            }
            catch (error) {
                // Non-critical, just log warning
                console.warn(`Failed to update access stats for ${movieId}:`, error);
            }
        });
    }
    /**
     * Safely extract milliseconds from a Firestore timestamp
     * Handles both web SDK Timestamps (with .toMillis()) and native plain objects
     */
    timestampToMillis(timestamp) {
        var _a, _b, _c;
        if (!timestamp)
            return 0;
        // Web SDK Timestamp with toMillis()
        if (typeof timestamp.toMillis === 'function') {
            return timestamp.toMillis();
        }
        // Native format: { _seconds, _nanoseconds } or { seconds, nanoseconds }
        const seconds = (_a = timestamp._seconds) !== null && _a !== void 0 ? _a : timestamp.seconds;
        if (typeof seconds === 'number') {
            const nanoseconds = (_c = (_b = timestamp._nanoseconds) !== null && _b !== void 0 ? _b : timestamp.nanoseconds) !== null && _c !== void 0 ? _c : 0;
            return seconds * 1000 + Math.floor(nanoseconds / 1000000);
        }
        // Plain number (already milliseconds)
        if (typeof timestamp === 'number') {
            return timestamp;
        }
        // Date object
        if (timestamp instanceof Date) {
            return timestamp.getTime();
        }
        // String format from Capacitor native adapter: "Timestamp(seconds=X, nanoseconds=Y)"
        if (typeof timestamp === 'string') {
            const match = timestamp.match(/Timestamp\(seconds=(\d+),\s*nanoseconds=(\d+)\)/);
            if (match) {
                const secs = parseInt(match[1], 10);
                const nanos = parseInt(match[2], 10);
                return secs * 1000 + Math.floor(nanos / 1000000);
            }
        }
        console.warn('Unknown timestamp format:', timestamp);
        return 0;
    }
    /**
     * Check if cached data is expired
     */
    isExpired(data) {
        const age = Date.now() - this.timestampToMillis(data.cachedAt);
        return age > data.ttl;
    }
    /**
     * Get age of cached data in human-readable format
     */
    getAge(data) {
        const ageMs = Date.now() - this.timestampToMillis(data.cachedAt);
        const ageDays = Math.floor(ageMs / (24 * 60 * 60 * 1000));
        if (ageDays === 0)
            return 'today';
        if (ageDays === 1)
            return '1 day';
        if (ageDays < 30)
            return `${ageDays} days`;
        const ageMonths = Math.floor(ageDays / 30);
        if (ageMonths === 1)
            return '1 month';
        return `${ageMonths} months`;
    }
    /**
     * Fetch movie from TMDB and format for cache
     */
    fetchFromTMDB(movieId) {
        var _a, _b, _c, _d;
        return __awaiter(this, void 0, void 0, function* () {
            try {
                // Call Firebase Functions API directly to get movie details
                // This avoids circular dependencies with web package
                const FUNCTIONS_URL = `https://us-central1-watchlists-1c639.cloudfunctions.net/getMovieDetails/${movieId}`;
                const response = yield fetch(`${FUNCTIONS_URL}?language=en-US&include_credits=true`);
                if (!response.ok) {
                    throw new Error(`Failed to fetch movie ${movieId}: ${response.statusText}`);
                }
                const tmdbData = yield response.json();
                // Extract directors from crew
                const directors = (((_a = tmdbData.credits) === null || _a === void 0 ? void 0 : _a.crew) || [])
                    .filter((c) => c.job === 'Director')
                    .map((d) => ({
                    id: d.id,
                    name: d.name,
                    profilePath: d.profile_path || null,
                }));
                // Get top 10 cast members
                const mainCast = (((_b = tmdbData.credits) === null || _b === void 0 ? void 0 : _b.cast) || [])
                    .slice(0, 10)
                    .map((a) => {
                    var _a;
                    return ({
                        id: a.id,
                        name: a.name,
                        character: a.character || '',
                        profilePath: a.profile_path || null,
                        order: (_a = a.order) !== null && _a !== void 0 ? _a : 999,
                    });
                });
                // Format for cache - IMPORTANT: Firestore doesn't accept undefined values
                // Use null for optional fields, and filter out undefined values
                const cacheData = {
                    id: movieId,
                    imdbId: tmdbData.imdb_id || null,
                    title: tmdbData.title || tmdbData.Title || 'Unknown',
                    originalTitle: tmdbData.original_title || null,
                    year: this.extractYear(tmdbData),
                    releaseDate: tmdbData.release_date || '',
                    posterPath: this.extractPosterPath(tmdbData),
                    backdropPath: tmdbData.backdrop_path || null,
                    overview: tmdbData.overview || tmdbData.Plot || '',
                    tagline: tmdbData.tagline || null,
                    runtime: tmdbData.runtime || 0,
                    voteAverage: tmdbData.vote_average || this.parseRating(tmdbData.imdbRating) || 0,
                    voteCount: tmdbData.vote_count || 0,
                    genres: tmdbData.genres || [],
                    directors,
                    mainCast,
                    originalLanguage: tmdbData.original_language || 'en',
                    productionCountries: ((_c = tmdbData.production_countries) === null || _c === void 0 ? void 0 : _c.map((c) => c.iso_3166_1)) || [],
                    spokenLanguages: ((_d = tmdbData.spoken_languages) === null || _d === void 0 ? void 0 : _d.map((l) => l.iso_639_1)) || [],
                    cachedAt: firebaseBase_1.Timestamp.now(),
                    ttl: this.CACHE_TTL,
                    version: this.CACHE_VERSION,
                    accessCount: 1,
                    lastAccessedAt: firebaseBase_1.Timestamp.now(),
                };
                return cacheData;
            }
            catch (error) {
                console.error(`Error fetching movie ${movieId} from TMDB:`, error);
                throw error;
            }
        });
    }
    /**
     * Fetch batch of movies from TMDB with rate limiting
     */
    fetchBatchFromTMDB(movieIds) {
        return __awaiter(this, void 0, void 0, function* () {
            const results = [];
            const BATCH_SIZE = 5; // Process 5 at a time to respect rate limits
            for (let i = 0; i < movieIds.length; i += BATCH_SIZE) {
                const batch = movieIds.slice(i, i + BATCH_SIZE);
                const promises = batch.map(id => this.fetchFromTMDB(id).catch(err => {
                    console.error(`Failed to fetch movie ${id}:`, err);
                    return null;
                }));
                const batchResults = yield Promise.all(promises);
                results.push(...batchResults.filter((r) => r !== null));
                // Small delay between batches to respect rate limits
                if (i + BATCH_SIZE < movieIds.length) {
                    yield this.sleep(100);
                }
            }
            return results;
        });
    }
    /**
     * Extract year from various date formats
     */
    extractYear(tmdbData) {
        // Try from release_date
        if (tmdbData.release_date) {
            const year = tmdbData.release_date.split('-')[0];
            if (year && /^\d{4}$/.test(year))
                return year;
        }
        // Try from Year field (OMDB format)
        if (tmdbData.Year) {
            const year = tmdbData.Year.split('–')[0].trim(); // Handle ranges like "2019–2020"
            if (/^\d{4}$/.test(year))
                return year;
        }
        // Default to current year
        return new Date().getFullYear().toString();
    }
    /**
     * Extract poster path from various formats
     */
    extractPosterPath(tmdbData) {
        if (tmdbData.poster_path) {
            return tmdbData.poster_path;
        }
        if (tmdbData.Poster && tmdbData.Poster !== 'N/A') {
            // OMDB format - extract path from full URL
            if (tmdbData.Poster.includes('image.tmdb.org')) {
                const match = tmdbData.Poster.match(/\/w\d+(.+)/);
                return match ? match[1] : null;
            }
        }
        return null;
    }
    /**
     * Parse rating from string format
     */
    parseRating(rating) {
        if (!rating)
            return 0;
        const parsed = parseFloat(rating);
        return isNaN(parsed) ? 0 : parsed;
    }
    /**
     * Sleep helper for rate limiting
     */
    sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
}
// Export singleton instance
exports.movieCacheService = new MovieCacheService();
